import Logger from './logger';
/** The name of the module */
export const name = 'Date';
/** Units for adding/substracting datetimes */
export var Unit;
(function (Unit) {
    Unit[Unit["Day"] = 0] = "Day";
    Unit[Unit["Hour"] = 1] = "Hour";
    Unit[Unit["Minute"] = 2] = "Minute";
    Unit[Unit["Second"] = 3] = "Second";
})(Unit || (Unit = {}));
const Duration = {
    [Unit.Second]: 1000,
    [Unit.Minute]: 1000 * 60,
    [Unit.Hour]: 1000 * 60 * 60,
    [Unit.Day]: 1000 * 60 * 60 * 24,
};
/**
 * Add a specified number of units to a provided Date object
 *
 * @param date Date object to add time to
 * @param unit Unit to add (e.g. second, minute)
 * @param amount Amount of units to add
 */
export function dateAdd(date, unit, amount) {
    return new Date(date.getTime() + amount * Duration[unit]);
}
/**
 * Subtract a specified number of units to a provided Date object
 *
 * @param date Date object to subtract time from
 * @param unit Unit to subtract (e.g. second, minute)
 * @param amount Amount of units to subtract
 */
export function dateSubtract(date, unit, amount) {
    return dateAdd(date, unit, amount * -1);
}
/**
 * Test if a provided value is a relative time string in the format of +### or -###
 *
 * @param testValue Value to test against
 *
 * @return boolean
 */
function isRelativeTimeString(testValue) {
    return typeof testValue !== 'number' && (testValue[0] === '+' || testValue[0] === '-');
}
/**
 * Parse a relative time string and return the actual time portion as an integer
 *
 * @param relativeTimeString A string in relative time string
 */
function getTimeFromRelativeTimeString(relativeTimeString) {
    // Since we can't type the format of string for a RelativeTimeString type, a type guard is used to validate the
    // string format
    if (isRelativeTimeString(relativeTimeString)) {
        return parseInt(relativeTimeString.substr(1), 10);
    }
    // String failed validation, so throw an error and return 0
    Logger.error(`Invalid relative time string provided: ${relativeTimeString}`);
    return 0;
}
/**
 * Utility method for guaranteeing that a Date object is being used
 *
 * @param date A Date object, timestamp, or relative string (e.g. +30, -600) to convert to a Date object
 */
export function getAsDate(date, unit = Unit.Second) {
    // If the date is already a Date variable, return it
    if (date instanceof Date) {
        return date;
    }
    // If the date is a relative string, then add/subtract the specified amount of time to it
    if (isRelativeTimeString(date)) {
        const relativeTime = getTimeFromRelativeTimeString(date);
        if (date[0] === '+') {
            return dateAdd(new Date(), unit, relativeTime);
        }
        if (date[0] === '-') {
            return dateSubtract(new Date(), unit, relativeTime);
        }
        // If the date is a number, then convert it to a JavaScript timestamp
    }
    else if (typeof date === 'number') {
        return new Date(date * 1000);
    }
    // Otherwise try to pass whatever was given to the Date constructor
    const dateObj = new Date(date);
    // If the result of .getTime() isn't a number, then the Date constructor wasn't able to parse the date
    if (Number.isNaN(dateObj.getTime())) {
        Logger.error(`Invalid date string provided: ${date}`);
    }
    return dateObj;
}
/**
 * Calculate the difference between two datetimes in milliseconds
 *
 * @param start - The start datetime for the diff
 * @param end The end datetime for the diff
 */
export function dateDiff(start, end) {
    const startDate = getAsDate(start);
    const endDate = getAsDate(end);
    return Math.floor(endDate.getTime() - startDate.getTime());
}
/**
 * Calculate the difference between now and a provided datetimes
 *
 * @param date The date to use for the diff
 */
export function dateDiffFromNow(date) {
    return dateDiff(new Date(), date);
}
/**
 * Accepts multiple dates and returns the max (latest) date object.
 *
 * @param dates An array of date objects to compare
 */
export function getMaxDate(...dates) {
    // Not quite what reduce is meant for, but it will help us select the max date value
    return dates.reduce((maxDate, currentDate) => {
        if (maxDate.getTime() > currentDate.getTime()) {
            return maxDate;
        }
        return currentDate;
    });
}
/**
 * Accepts multiple dates and returns the min (earliest) date object.
 *
 * @param dates An array of date objects to compare
 */
export function getMinDate(...dates) {
    // Not quite what reduce is meant for, but it will help us select the min date value
    return dates.reduce((minDate, currentDate) => {
        if (minDate.getTime() < currentDate.getTime()) {
            return minDate;
        }
        return currentDate;
    });
}
/**
 * Formats a provided duration in milliseconds into the corresponding days, hours, minutes, and seconds used to
 * represent that duration
 *
 * @param duration Duration in milliseconds
 */
export function durationFormatAsObject(duration) {
    let days = 0;
    let hours = 0;
    let minutes = 0;
    let seconds = 0;
    // If duration is set, then calculate the units
    if (duration > 0) {
        days = Math.floor(duration / Duration[Unit.Day]);
        hours = Math.floor((duration - (days * Duration[Unit.Day])) / Duration[Unit.Hour]);
        minutes = Math.floor((duration - (days * Duration[Unit.Day]) - (hours * Duration[Unit.Hour])) / Duration[Unit.Minute]);
        seconds = Math.floor((duration - (days * Duration[Unit.Day]) - (hours * Duration[Unit.Hour]) - (minutes * Duration[Unit.Minute])) / Duration[Unit.Second]);
    }
    return {
        duration,
        days,
        hours,
        minutes,
        seconds,
    };
}
